<script setup>
import { computed, onMounted, ref, nextTick } from 'vue';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import { useAlert } from 'dashboard/composables';
import { parseAPIErrorResponse } from 'dashboard/store/utils/api';
import { useI18n } from 'vue-i18n';
import AilabDocumentAPI from 'dashboard/api/ailab/document';
import AilabAiHealthAPI from 'dashboard/api/ailab/aiHealth';

import DeleteDialog from 'dashboard/components-next/ailab/pageComponents/DeleteDialog.vue';
import DocumentCard from 'dashboard/components-next/ailab/assistant/DocumentCard.vue';
import PageLayout from 'dashboard/components-next/ailab/PageLayout.vue';
import AilabPaywall from 'dashboard/components-next/ailab/pageComponents/Paywall.vue';
import RelatedResponses from 'dashboard/components-next/ailab/pageComponents/document/RelatedResponses.vue';
import CreateDocumentDialog from 'dashboard/components-next/ailab/pageComponents/document/CreateDocumentDialog.vue';
import CrawlDetailsDialog from 'dashboard/components-next/ailab/pageComponents/document/CrawlDetailsDialog.vue';
import DocumentStatusSelector from 'dashboard/components-next/ailab/pageComponents/document/DocumentStatusSelector.vue';
import CrawlHealthPanel from 'dashboard/components-next/ailab/pageComponents/document/CrawlHealthPanel.vue';
import AiHealthPanel from 'dashboard/components-next/ailab/pageComponents/document/AiHealthPanel.vue';
import AssistantSelector from 'dashboard/components-next/ailab/pageComponents/AssistantSelector.vue';
import DocumentPageEmptyState from 'dashboard/components-next/ailab/pageComponents/emptyStates/DocumentPageEmptyState.vue';
import FeatureSpotlightPopover from 'dashboard/components-next/feature-spotlight/FeatureSpotlightPopover.vue';
import LimitBanner from 'dashboard/components-next/ailab/pageComponents/document/LimitBanner.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const store = useStore();
const { t } = useI18n();

const uiFlags = useMapGetter('ailabDocuments/getUIFlags');
const documents = useMapGetter('ailabDocuments/getRecords');
const assistants = useMapGetter('ailabAssistants/getRecords');
const isFetching = computed(() => uiFlags.value.fetchingList);
const documentsMeta = useMapGetter('ailabDocuments/getMeta');
const selectedAssistant = ref('all');
const selectedStatus = ref('all');
const metricsLoading = ref(false);
const crawlMetrics = ref({});
const aiHealthLoading = ref(false);
const aiHealthMetrics = ref({});

const selectedDocument = ref(null);
const deleteDocumentDialog = ref(null);
const retryingDocumentId = ref(null);

const handleDelete = () => {
  deleteDocumentDialog.value.dialogRef.open();
};

const showRelatedResponses = ref(false);
const showCreateDialog = ref(false);
const showCrawlDetails = ref(false);
const createDocumentDialog = ref(null);
const relationQuestionDialog = ref(null);
const crawlDetailsDialog = ref(null);

const shouldShowAssistantSelector = computed(() => {
  if (assistants.value.length === 0) return false;

  return !isFetching.value;
});

const shouldShowStatusSelector = computed(() => !isFetching.value);

const selectedAssistantLabel = computed(() => {
  if (selectedAssistant.value === 'all') {
    return t('AILAB.DOCUMENTS.FILTER.ALL_ASSISTANTS');
  }
  const found = assistants.value.find(a => a.id === selectedAssistant.value);
  return found?.name || '';
});

const selectedStatusLabel = computed(() => {
  const v = selectedStatus.value?.toString() || 'all';
  if (v === 'all') return t('AILAB.DOCUMENTS.FILTER.ALL');
  if (v === 'retryable') return t('AILAB.DOCUMENTS.FILTER.RETRYABLE');
  if (v === 'available') return t('AILAB.DOCUMENTS.STATUS.AVAILABLE');
  if (v === 'in_progress') return t('AILAB.DOCUMENTS.STATUS.IN_PROGRESS');
  if (v === 'blocked') return t('AILAB.DOCUMENTS.STATUS.BLOCKED');
  if (v === 'failed') return t('AILAB.DOCUMENTS.STATUS.FAILED');
  return v;
});

const filterSummary = computed(() =>
  t('AILAB.DOCUMENTS.FILTER.SUMMARY', {
    assistant: selectedAssistantLabel.value || '-',
    status: selectedStatusLabel.value || '-',
  })
);

const handleShowRelatedDocument = () => {
  showRelatedResponses.value = true;
  nextTick(() => relationQuestionDialog.value.dialogRef.open());
};
const handleShowCrawlDetails = () => {
  showCrawlDetails.value = true;
  nextTick(() => crawlDetailsDialog.value.dialogRef.open());
};
const handleCreateDocument = () => {
  showCreateDialog.value = true;
  nextTick(() => createDocumentDialog.value.dialogRef.open());
};

const handleRelatedResponseClose = () => {
  showRelatedResponses.value = false;
};

const handleCrawlDetailsClose = () => {
  showCrawlDetails.value = false;
};

const handleCreateDialogClose = () => {
  showCreateDialog.value = false;
};

const handleAction = ({ action, id }) => {
  selectedDocument.value = documents.value.find(
    ailabDocument => id === ailabDocument.id
  );

  nextTick(() => {
    if (action === 'delete') {
      handleDelete();
    } else if (action === 'viewRelatedQuestions') {
      handleShowRelatedDocument();
    } else if (action === 'viewCrawlDetails') {
      handleShowCrawlDetails();
    } else if (action === 'retry') {
      handleRetry(id);
    }
  });
};

const handleRetry = async id => {
  try {
    retryingDocumentId.value = id;
    await store.dispatch('ailabDocuments/retry', id);
    useAlert(
      t('AILAB.DOCUMENTS.RETRY.SUCCESS_MESSAGE') ||
        'Retry scheduled successfully'
    );
  } catch (error) {
    const errorMessage =
      parseAPIErrorResponse(error) ||
      t('AILAB.DOCUMENTS.RETRY.ERROR_MESSAGE') ||
      'Failed to retry';
    useAlert(errorMessage);
  } finally {
    retryingDocumentId.value = null;
  }
};

const fetchDocuments = (page = 1) => {
  const filterParams = { page };

  if (selectedAssistant.value !== 'all') {
    filterParams.assistantId = selectedAssistant.value;
  }
  if (selectedStatus.value && selectedStatus.value !== 'all') {
    filterParams.status = selectedStatus.value;
  }
  store.dispatch('ailabDocuments/get', filterParams);
};

const handleAssistantFilterChange = assistant => {
  selectedAssistant.value = assistant;
  fetchDocuments();
};

const handleStatusFilterChange = status => {
  selectedStatus.value = status;
  fetchDocuments();
};

const applyQuickStatus = status => {
  selectedStatus.value = status;
  fetchDocuments();
};

const onPageChange = page => fetchDocuments(page);

const onDeleteSuccess = () => {
  if (documents.value?.length === 0 && documentsMeta.value?.page > 1) {
    onPageChange(documentsMeta.value.page - 1);
  }
};

const refreshAiHealth = async () => {
  try {
    aiHealthLoading.value = true;
    const response = await AilabAiHealthAPI.get({ hours: 24 });
    aiHealthMetrics.value = response?.data || {};
  } catch (error) {
    useAlert(
      parseAPIErrorResponse(error) ||
        t('AILAB.AI_HEALTH.ERROR_MESSAGE') ||
        'Failed to load AI health metrics'
    );
  } finally {
    aiHealthLoading.value = false;
  }
};

onMounted(() => {
  if (!assistants.value.length) {
    store.dispatch('ailabAssistants/get');
  }
  fetchDocuments();
  refreshMetrics();
  refreshAiHealth();
});

const refreshMetrics = async () => {
  try {
    metricsLoading.value = true;
    const response = await AilabDocumentAPI.metrics({ hours: 24 });
    crawlMetrics.value = response.data || {};
  } catch (error) {
    // non-blocking
    useAlert(
      parseAPIErrorResponse(error) ||
        t('AILAB.DOCUMENTS.HEALTH.ERROR_MESSAGE') ||
        'Failed to load metrics'
    );
  } finally {
    metricsLoading.value = false;
  }
};
</script>

<template>
  <PageLayout
    :header-title="$t('AILAB.DOCUMENTS.HEADER')"
    :button-label="$t('AILAB.DOCUMENTS.ADD_NEW')"
    :button-policy="['administrator']"
    :total-count="documentsMeta.totalCount"
    :current-page="documentsMeta.page"
    :show-pagination-footer="!isFetching && !!documents.length"
    :is-fetching="isFetching"
    :is-empty="!documents.length"
    :feature-flag="FEATURE_FLAGS.CAPTAIN"
    @update:current-page="onPageChange"
    @click="handleCreateDocument"
  >
    <template #headerIcon>
      <div
        class="w-11 h-11 rounded-xl bg-slate-50 border border-slate-200 flex items-center justify-center text-slate-900 flex-shrink-0"
      >
        <span class="i-lucide-file-text size-5" />
      </div>
    </template>

    <template #headerSubtitle>
      <div class="text-sm text-slate-600">
        Connect knowledge sources and monitor crawl & health.
      </div>
    </template>

    <template #knowMore>
      <FeatureSpotlightPopover
        :button-label="$t('AILAB.HEADER_KNOW_MORE')"
        :title="$t('AILAB.DOCUMENTS.EMPTY_STATE.FEATURE_SPOTLIGHT.TITLE')"
        :note="$t('AILAB.DOCUMENTS.EMPTY_STATE.FEATURE_SPOTLIGHT.NOTE')"
        fallback-thumbnail="/assets/images/dashboard/ailab/document-popover-light.svg"
        fallback-thumbnail-dark="/assets/images/dashboard/ailab/document-popover-dark.svg"
      />
    </template>

    <template #emptyState>
      <DocumentPageEmptyState @click="handleCreateDocument" />
    </template>

    <template #paywall>
      <AilabPaywall />
    </template>

    <template #controls>
      <div class="mb-4 -mt-1 flex flex-wrap gap-3">
        <AssistantSelector
          v-if="shouldShowAssistantSelector"
          :assistant-id="selectedAssistant"
          @update="handleAssistantFilterChange"
        />
        <DocumentStatusSelector
          v-if="shouldShowStatusSelector"
          :status="selectedStatus"
          @update="handleStatusFilterChange"
        />
      </div>
      <div class="mb-4 -mt-1 flex items-center justify-between gap-3">
        <div class="flex items-center gap-2">
          <Button
            :label="t('AILAB.DOCUMENTS.FILTER.ALL')"
            size="sm"
            color="slate"
            :variant="selectedStatus === 'all' ? 'solid' : 'outline'"
            @click="applyQuickStatus('all')"
          />
          <Button
            :label="t('AILAB.DOCUMENTS.FILTER.RETRYABLE')"
            size="sm"
            color="slate"
            :variant="selectedStatus === 'retryable' ? 'solid' : 'outline'"
            @click="applyQuickStatus('retryable')"
          />
        </div>
        <div class="text-xs text-slate-500 truncate">
          {{ filterSummary }}
        </div>
      </div>
    </template>

    <template #body>
      <LimitBanner class="mb-5" />
      <AiHealthPanel
        :metrics="aiHealthMetrics"
        :loading="aiHealthLoading"
        @refresh="refreshAiHealth"
      />
      <CrawlHealthPanel
        :metrics="crawlMetrics"
        :loading="metricsLoading"
        @refresh="refreshMetrics"
      />

      <div class="flex flex-col gap-4">
        <DocumentCard
          v-for="doc in documents"
          :id="doc.id"
          :key="doc.id"
          :name="doc.name || doc.external_link"
          :external-link="doc.external_link"
          :assistant="doc.assistant"
          :created-at="doc.created_at"
          :status="doc.status"
          :can-retry="doc.can_retry"
          :crawl-error="doc.crawl_error"
          :crawl-error-at="doc.crawl_error_at"
          :rejected-count="doc?.metadata?.firecrawl?.rejected_count || 0"
          :is-retrying="retryingDocumentId === doc.id"
          @action="handleAction"
        />
      </div>
    </template>

    <RelatedResponses
      v-if="showRelatedResponses"
      ref="relationQuestionDialog"
      :ailab-document="selectedDocument"
      @close="handleRelatedResponseClose"
    />
    <CrawlDetailsDialog
      v-if="showCrawlDetails"
      ref="crawlDetailsDialog"
      :ailab-document="selectedDocument"
      @close="handleCrawlDetailsClose"
    />
    <CreateDocumentDialog
      v-if="showCreateDialog"
      ref="createDocumentDialog"
      @close="handleCreateDialogClose"
    />
    <DeleteDialog
      v-if="selectedDocument"
      ref="deleteDocumentDialog"
      :entity="selectedDocument"
      type="Documents"
      @delete-success="onDeleteSuccess"
    />
  </PageLayout>
</template>
